import * as THREE from 'three';
import { EYE_HEIGHT, type PlayerEntity } from '../player/PlayerEntity';
import { createSprayCan, type SprayCanModel } from '../player/SprayCan';
import type { BuiltLocation } from '../world/Location';

export type CameraMode = 'first' | 'third';

const THIRD_PERSON_DISTANCE = 4.4;

/**
 * Owns the perspective camera and both view rigs.
 *
 * First person: camera at eye height with a viewmodel spray can that bobs
 * while walking and shakes while spraying.
 * Third person: orbit behind the character with collision so the camera
 * never clips through buildings or terrain. Aiming raycasts from the screen
 * center in both modes, so painting behaves identically.
 */
export class CameraManager {
  readonly camera: THREE.PerspectiveCamera;
  mode: CameraMode = 'first';
  /** First-person viewmodel can (added to the camera). */
  readonly viewCan: SprayCanModel;

  private bobPhase = 0;
  private shakeTime = 0;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(72, aspect, 0.08, 900);
    this.viewCan = createSprayCan(0.62);
    this.viewCan.group.position.set(0.24, -0.33, -0.5);
    this.viewCan.group.rotation.set(-0.55, -0.25, 0.08);
    this.camera.add(this.viewCan.group);
    // Subtle fill so the viewmodel reads in night scenes; the sharp falloff
    // keeps it from lighting the world.
    const fill = new THREE.PointLight(0xfff2e0, 0.7, 1.6, 2);
    fill.position.set(0.05, 0.1, -0.2);
    this.camera.add(fill);
  }

  toggle(): CameraMode {
    this.mode = this.mode === 'first' ? 'third' : 'first';
    this.viewCan.group.visible = this.mode === 'first';
    return this.mode;
  }

  update(player: PlayerEntity, location: BuiltLocation, dt: number): void {
    const euler = new THREE.Euler(player.pitch, player.yaw, 0, 'YXZ');
    if (this.mode === 'first') {
      this.camera.position.set(
        player.position.x,
        player.position.y + EYE_HEIGHT,
        player.position.z,
      );
      this.camera.quaternion.setFromEuler(euler);
      this.updateViewmodel(player, dt);
    } else {
      const head = new THREE.Vector3(
        player.position.x,
        player.position.y + EYE_HEIGHT - 0.1,
        player.position.z,
      );
      const back = new THREE.Vector3(0, 0, 1).applyEuler(euler);
      const desired = head
        .clone()
        .addScaledVector(back, THIRD_PERSON_DISTANCE)
        .add(new THREE.Vector3(0, 0.35, 0));
      const clamped = this.resolveCameraCollision(head, desired, location);
      this.camera.position.copy(clamped);
      this.camera.quaternion.setFromEuler(euler);
    }
  }

  private updateViewmodel(player: PlayerEntity, dt: number): void {
    this.bobPhase += dt * (2 + player.speed * 1.9);
    const bobAmp = Math.min(player.speed / 7, 1) * 0.012;
    let x = 0.24 + Math.sin(this.bobPhase) * bobAmp;
    let y = -0.33 + Math.abs(Math.cos(this.bobPhase)) * bobAmp * 1.4;
    if (player.isSpraying) {
      this.shakeTime += dt;
      x += Math.sin(this.shakeTime * 55) * 0.0035;
      y += Math.cos(this.shakeTime * 47) * 0.0035;
    } else {
      this.shakeTime = 0;
    }
    this.viewCan.group.position.set(x, y, -0.5);
  }

  /** March from the player's head toward the desired camera spot; stop before
   * entering a collider or the ground. */
  private resolveCameraCollision(
    head: THREE.Vector3,
    desired: THREE.Vector3,
    location: BuiltLocation,
  ): THREE.Vector3 {
    const steps = 16;
    const p = new THREE.Vector3();
    let best = head.clone().lerp(desired, 0.12);
    for (let i = 1; i <= steps; i++) {
      p.lerpVectors(head, desired, i / steps);
      const ground = location.groundHeight(p.x, p.z);
      if (p.y < ground + 0.25) break;
      let blocked = false;
      for (const box of location.colliders) {
        if (
          p.x > box.min.x - 0.2 &&
          p.x < box.max.x + 0.2 &&
          p.y > box.min.y - 0.2 &&
          p.y < box.max.y + 0.2 &&
          p.z > box.min.z - 0.2 &&
          p.z < box.max.z + 0.2
        ) {
          blocked = true;
          break;
        }
      }
      if (blocked) break;
      best = p.clone();
    }
    return best;
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }
}
